import React, { useState, useEffect } from "react";
import { getPasswordValidationMessage } from "../../../utils/passwordPolicy";
import { LOGO_URL as logo } from "../../../services";
import CustomInput from "../../../components/CustomInput/CustomInput";
import { useNavigate } from "react-router-dom";
import Spinner from "../../../components/Spinner/Spinner";
import Services from "../../../services/Services";
import SuccessModal from "../../../components/SuccessModal/SuccessModal";
import { klavyeIleTikla } from "../../../utils/a11y"; // FE-78: tıklanabilir öğelerde klavye desteği
import { COK_DENEME_MESAJI, cokDenemeMi } from "../../../utils/apiOturum";

// 26.10.2026 (güvenlik manifesti A3): BE aynı e-postaya 15 dakikada 3 sıfırlama kabul ediyor → "Tekrar Gönder"
// her gönderimden sonra en az bu kadar saniye kapalı; sınır aşılınca Türkçe mesaj.
const TEKRAR_GONDER_SN = 60;

const ForgotPasswordForm = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
    const [token, setToken] = useState("");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [userID, setUserID] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [errors, setErrors] = useState({ email: "", otp: "", password: "" });
    const [showSuccess, setShowSuccess] = useState(false);
    const [tekrarKalan, setTekrarKalan] = useState(0); // "Tekrar Gönder" bekleme süresi (sn)

    useEffect(() => {
        if (tekrarKalan <= 0) return undefined;
        const t = setTimeout(() => setTekrarKalan((k) => k - 1), 1000);
        return () => clearTimeout(t);
    }, [tekrarKalan]);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    useEffect(() => {
        Services.GetToken()
            .then((res) => {
                if (res.data.status) {
                    setToken(res.data.result.token);
                }
            })
    }, []);

    const validateEmail = () => {
        if (!email) {
            setErrors((e) => ({ ...e, email: "E-posta adresi gereklidir." }));
            return false;
        }
        if (!emailRegex.test(email)) {
            setErrors((e) => ({ ...e, email: "Geçerli bir e-posta giriniz." }));
            return false;
        }
        setErrors((e) => ({ ...e, email: "" }));
        return true;
    };

    const handleEmailSubmit = () => {
        if (!validateEmail()) return;
        if (!token) {
            setErrors((e) => ({ ...e, email: "Bir hata oluştu. Lütfen sayfayı yenileyin." }));
            return;
        }

        setIsLoading(true);
        Services.ForgotPassword(token, email)
            .then((res) => {
                setIsLoading(false);
                if (res.data.status) {
                    setStep(2);
                    setTekrarKalan(TEKRAR_GONDER_SN);
                    setErrors((e) => ({ ...e, email: "" }));
                } else {
                    setErrors((e) => ({ ...e, email: res.data.message || "Bu e-posta adresi ile kayıtlı bir hesap bulunamadı." }));
                }
            })
            .catch((err) => {
                setIsLoading(false);
                setErrors((e) => ({ ...e, email: "Bir hata oluştu. Lütfen tekrar deneyin." }));
            });
    };

    const handleResendOTP = () => {
        if (!token || isResending || tekrarKalan > 0) return;

        setIsResending(true);
        Services.ForgotPassword(token, email)
            .then((res) => {
                setIsResending(false);
                if (res.data.status) {
                    setTekrarKalan(TEKRAR_GONDER_SN);
                } else if (cokDenemeMi(res.data.message)) {
                    setTekrarKalan(TEKRAR_GONDER_SN);
                    setErrors((e) => ({ ...e, otp: COK_DENEME_MESAJI }));
                } else {
                    setErrors((e) => ({ ...e, otp: "Kod gönderilemedi. Lütfen tekrar deneyin." }));
                }
            })
            .catch((err) => {
                setIsResending(false);
                setErrors((e) => ({ ...e, otp: "Kod gönderilemedi. Lütfen tekrar deneyin." }));
            });
    };

    const handleOtpChange = (index, value) => {
        if (value.length > 1) return;
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setErrors((e) => ({ ...e, otp: "" }));

        // Auto focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-input-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-input-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 6);
        if (!/^\d+$/.test(pastedData)) return;

        const newOtp = [...otp];
        for (let i = 0; i < pastedData.length; i++) {
            newOtp[i] = pastedData[i];
        }
        setOtp(newOtp);
    };

    const handleOtpSubmit = () => {
        const otpValue = otp.join("");
        if (otpValue.length !== 6) {
            setErrors((e) => ({ ...e, otp: "Lütfen 6 haneli kodu giriniz." }));
            return;
        }
        if (!token) {
            setErrors((e) => ({ ...e, otp: "Bir hata oluştu. Lütfen sayfayı yenileyin." }));
            return;
        }

        setIsLoading(true);
        Services.ConfirmOTP(token, email, otpValue)
            .then((res) => {
                setIsLoading(false);
                if (res.data.status && res.data.result?.userID) {
                    setUserID(res.data.result.userID);
                    setStep(3);
                    setErrors((e) => ({ ...e, otp: "" }));
                } else {
                    setErrors((e) => ({ ...e, otp: res.data.message || "Geçersiz doğrulama kodu." }));
                }
            })
            .catch((err) => {
                setIsLoading(false);
                setErrors((e) => ({ ...e, otp: "Bir hata oluştu. Lütfen tekrar deneyin." }));
            });
    };

    const handlePasswordSubmit = () => {
        if (!newPassword) {
            setErrors((e) => ({ ...e, password: "Yeni şifre gereklidir." }));
            return;
        }
        // Panelin ORTAK şifre kuralı (Profil > Güvenlik ile aynı): eskiden burada yalnız
        // 6 karakter aranıyor ve zayıf şifre belirlenebiliyordu.
        const passwordRuleMessage = getPasswordValidationMessage(newPassword);
        if (passwordRuleMessage) {
            setErrors((e) => ({ ...e, password: passwordRuleMessage }));
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrors((e) => ({ ...e, password: "Şifreler eşleşmiyor." }));
            return;
        }
        if (!token || !userID) {
            setErrors((e) => ({ ...e, password: "Bir hata oluştu. Lütfen işlemi baştan başlatın." }));
            return;
        }

        setIsLoading(true);
        Services.UpdateUserPassword(token, userID, newPassword)
            .then((res) => {
                setIsLoading(false);
                if (res.data.status) {
                    setShowSuccess(true);
                } else {
                    setErrors((e) => ({ ...e, password: res.data.message || "Şifre güncellenemedi. Lütfen tekrar deneyin." }));
                }
            })
            .catch((err) => {
                setIsLoading(false);
                setErrors((e) => ({ ...e, password: "Bir hata oluştu. Lütfen tekrar deneyin." }));
            });
    };

    const getStepTitle = () => {
        switch (step) {
            case 1:
                return "Şifremi Unuttum";
            case 2:
                return "Doğrulama Kodu";
            case 3:
                return "Yeni Şifre";
            default:
                return "";
        }
    };

    const getStepDescription = () => {
        switch (step) {
            case 1:
                return "Şifrenizi sıfırlamak için e-posta adresinizi girin.";
            case 2:
                return `${email} adresine gönderilen 6 haneli kodu girin.`;
            case 3:
                return "Yeni şifrenizi belirleyin.";
            default:
                return "";
        }
    };

    return (
        <div className="forgot-password-form">
            <div className="logo-container">
                <img src={logo} alt="Logo" />
                <p
                    style={{
                        borderBottom: "1px solid rgba(236, 236, 236, 1)",
                        paddingBottom: "20px"
                    }}
                >
                    Rahatlayın, güvenle depolayın! 💚
                </p>
            </div>

            <div className="hello-message">
                <span
                    style={{
                        fontWeight: "600",
                        color: "rgba(10, 34, 82, 1)",
                        fontSize: "32px"
                    }}
                >
                    {getStepTitle()}
                </span>
                <span
                    style={{
                        fontWeight: "500",
                        color: "rgba(159, 164, 170, 1)",
                        fontSize: "16px"
                    }}
                >
                    {getStepDescription()}
                </span>
            </div>

            {/* Step 1: Email */}
            {step === 1 && (
                <div className="forgot-password-form-input-holder">
                    <CustomInput
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setErrors((prev) => ({ ...prev, email: "" }));
                        }}
                        placeholder={"E-posta Adresi"}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && email) {
                                handleEmailSubmit();
                            }
                        }}
                    />
                    {errors.email && (
                        <span className="error-text">{errors.email}</span>
                    )}

                    {!isLoading ? (
                        <button
                            className="forgot-password-button"
                            disabled={!email}
                            onClick={handleEmailSubmit}
                        >
                            Devam Et
                        </button>
                    ) : (
                        <button
                            className="forgot-password-button"
                            disabled
                            style={{
                                justifyContent: "center",
                                alignItems: "center",
                                display: "flex"
                            }}
                        >
                            <Spinner size={25} />
                        </button>
                    )}
                </div>
            )}

            {/* Step 2: OTP */}
            {step === 2 && (
                <div className="forgot-password-form-input-holder">
                    <div className="otp-container">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                id={`otp-input-${index}`}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                onPaste={handleOtpPaste}
                                className="otp-input"
                                autoFocus={index === 0}
                            />
                        ))}
                    </div>
                    {errors.otp && (
                        <span className="error-text">{errors.otp}</span>
                    )}

                    <span role="button" tabIndex={0} onKeyDown={klavyeIleTikla}
                        className="update-phone"
                        onClick={() => {
                            setStep(1);
                            setOtp(["", "", "", "", "", ""]);
                            setErrors((prev) => ({ ...prev, otp: "" }));
                        }}
                    >
                        E-posta adresimi değiştir
                    </span>

                    <span className="resend-otp-code">
                        <span style={{ color: "rgba(89, 89, 90, 1)" }}>Kod gelmedi mi? </span>
                        {tekrarKalan > 0 && !isResending ? (
                            <span style={{ color: "rgba(159, 164, 170, 1)", fontWeight: "600" }}>
                                Tekrar Gönder ({tekrarKalan} sn)
                            </span>
                        ) : !isResending ? (
                            <span role="button" tabIndex={0} onKeyDown={klavyeIleTikla}
                                style={{
                                    color: "rgba(80, 185, 100, 1)",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                                onClick={handleResendOTP}
                            >
                                Tekrar Gönder
                            </span>
                        ) : (
                            <span
                                style={{
                                    color: "rgba(159, 164, 170, 1)",
                                    fontWeight: "600"
                                }}
                            >
                                Gönderiliyor...
                            </span>
                        )}
                    </span>

                    {!isLoading ? (
                        <button
                            className="forgot-password-button"
                            disabled={otp.join("").length !== 6}
                            onClick={handleOtpSubmit}
                        >
                            Doğrula
                        </button>
                    ) : (
                        <button
                            className="forgot-password-button"
                            disabled
                            style={{
                                justifyContent: "center",
                                alignItems: "center",
                                display: "flex"
                            }}
                        >
                            <Spinner size={25} />
                        </button>
                    )}
                </div>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
                <div className="forgot-password-form-input-holder">
                    <CustomInput
                        value={newPassword}
                        onChange={(e) => {
                            setNewPassword(e.target.value);
                            setErrors((prev) => ({ ...prev, password: "" }));
                        }}
                        placeholder={"Yeni Şifre"}
                        type="password"
                    />
                    <CustomInput
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            setErrors((prev) => ({ ...prev, password: "" }));
                        }}
                        placeholder={"Yeni Şifre (Tekrar)"}
                        type="password"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && newPassword && confirmPassword) {
                                handlePasswordSubmit();
                            }
                        }}
                    />
                    {errors.password && (
                        <span className="error-text">{errors.password}</span>
                    )}

                    {!isLoading ? (
                        <button
                            className="forgot-password-button"
                            disabled={!newPassword || !confirmPassword}
                            onClick={handlePasswordSubmit}
                        >
                            Şifremi Değiştir
                        </button>
                    ) : (
                        <button
                            className="forgot-password-button"
                            disabled
                            style={{
                                justifyContent: "center",
                                alignItems: "center",
                                display: "flex"
                            }}
                        >
                            <Spinner size={25} />
                        </button>
                    )}
                </div>
            )}

            <div className="have-account">
                <span
                    style={{
                        color: "rgba(159, 164, 170, 1)",
                        fontWeight: "500"
                    }}
                >
                    Şifrenizi hatırladınız mı?
                </span>
                <span role="button" tabIndex={0} onKeyDown={klavyeIleTikla}
                    onClick={() => navigate("/")}
                    style={{
                        color: "rgba(80, 185, 100, 1)",
                        fontWeight: "600",
                        cursor: "pointer"
                    }}
                >
                    Giriş Yap
                </span>
            </div>

            <span className="copyright">
                ©Copyright 2025. All Right Reserved
            </span>

            <SuccessModal
                isOpen={showSuccess}
                title="Şifreniz Güncellendi"
                message="Şifreniz başarıyla değiştirildi. Yeni şifrenizle giriş yapabilirsiniz."
                onConfirm={() => {
                    setShowSuccess(false);
                    navigate("/");
                }}
                onConfirmText="Giriş Yap"
            />
        </div>
    );
};

export default ForgotPasswordForm;
